import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { showSuccess } from '../../shared/ui/appToast';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import { sanitizeCsvFileName } from '../../shared/csv-core/domain/csvFileName';
import type { BranchSalesByProductRow } from '../domain/reportsTypes';
import { formatMoney } from '../../shared/format/public-api';
import { formatMoney0, formatMoney2, formatPct1 } from './salesByProductMetrics';

type PersonalRankingRow = {
  displayName: string;
  salesTotal: number;
  salesCount: number;
  ticketAverage: number;
  sharePct: number;
  fiscalSalesCount: number;
  nonFiscalSalesCount: number;
  pendingPayments: number;
  margenReposicionTotal: number;
};

function csvEscape(value: string | number): string {
  const str = String(value ?? '');
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function csvRow(values: Array<string | number>): string {
  return values.map(csvEscape).join(',') + '\n';
}

function downloadBlobWeb(filename: string, content: string): void {
  if (Platform.OS !== 'web' || typeof document === 'undefined' || typeof Blob === 'undefined') return;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function writeAndShareNative(filename: string, content: string, dialogTitle: string): Promise<void> {
  const uri = FileSystem.cacheDirectory + filename;
  await FileSystem.writeAsStringAsync(uri, content, { encoding: FileSystem.EncodingType.UTF8 });
  const canShare = await Sharing.isAvailableAsync();
  if (!canShare) {
    throw new Error('No hay una app para compartir el archivo en esta compu.');
  }
  await Sharing.shareAsync(uri, {
    mimeType: 'text/csv',
    dialogTitle,
    UTI: 'public.comma-separated-values-text',
  });
}

async function exportCsv(rawFilename: string, content: string, dialogTitle: string): Promise<void> {
  const filename = sanitizeCsvFileName(rawFilename);
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    downloadBlobWeb(filename, content);
    showSuccess('Exportado', 'Se descargó el archivo CSV.');
    return;
  }
  await writeAndShareNative(filename, content, dialogTitle);
}

export async function exportSalesByProductCsv(params: {
  periodLabel: string;
  rows: BranchSalesByProductRow[];
  sumLineTotal: number;
  sumQuantity: number;
  /** false si el listado mezcla productos con distinta unidad de medida (la suma de cantidades no es comparable). */
  uniformUnitOfMeasure?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}): Promise<void> {
  const { periodLabel, rows, sumLineTotal, sumQuantity, uniformUnitOfMeasure = true, page, pageSize, totalCount } = params;
  let csv = '';
  csv += csvRow(['Ventas por ítem — Vuelto']);
  csv += csvRow(['Período', periodLabel]);
  csv += csvRow([]);
  csv += csvRow([
    'Puesto',
    'Clase ABC',
    'Producto',
    'SKU',
    'Tipo',
    'Categoría',
    'Cantidad',
    'Unidad',
    'Importe',
    '% sobre importe total',
    '% sobre cantidad total',
    'Precio medio',
  ]);

  let accumulated = 0;
  rows.forEach((r, i) => {
    const rank = page * pageSize + i + 1;
    const contributionPct = sumLineTotal > 0 ? (r.lineTotal / sumLineTotal) * 100 : 0;
    accumulated += contributionPct;
    let abcClass: 'A' | 'B' | 'C' = 'C';
    if (accumulated <= 80) abcClass = 'A';
    else if (accumulated <= 95) abcClass = 'B';
    const avg = r.quantity > 0 ? r.lineTotal / r.quantity : null;
    const pct$ = formatPct1(sumLineTotal > 0 ? (r.lineTotal / sumLineTotal) * 100 : null);
    const pctU = formatPct1(sumQuantity > 0 ? (r.quantity / sumQuantity) * 100 : null);
    csv += csvRow([
      rank,
      abcClass,
      r.label,
      r.sku ?? '',
      r.itemType === 'service' ? 'Servicio' : 'Producto',
      r.categoryId ?? '',
      formatMoney2(r.quantity),
      normalizeUnitOfMeasure(r.unitOfMeasure),
      formatMoney0(r.lineTotal),
      pct$,
      pctU,
      avg != null ? formatMoney0(avg) : '',
    ]);
  });

  csv += csvRow([]);
  csv += csvRow([
    'Total en listado',
    '',
    '',
    '',
    '',
    '',
    formatMoney2(sumQuantity),
    uniformUnitOfMeasure ? normalizeUnitOfMeasure(rows[0]?.unitOfMeasure ?? null) : 'unidades mixtas',
    formatMoney0(sumLineTotal),
    '',
    '',
    '',
  ]);
  csv += csvRow(['Total general de ítems', totalCount]);
  csv += csvRow(['Página', `${page + 1} (${pageSize} por página)`]);

  const filename = `vuelto-ventas-por-item-${periodLabel.replace(/\s+/g, '_')}.csv`;
  await exportCsv(filename, csv, 'Exportar ventas por ítem');
}

export async function exportPersonalRankingCsv(params: {
  periodLabel: string;
  rows: PersonalRankingRow[];
}): Promise<void> {
  const { periodLabel, rows } = params;
  let csv = '';
  csv += csvRow(['Ranking de personal — Vuelto']);
  csv += csvRow(['Período', periodLabel]);
  csv += csvRow([]);
  csv += csvRow([
    'Puesto',
    'Operador',
    'Ventas',
    'Cantidad de ventas',
    'Ticket promedio',
    'Participación %',
    'Ventas fiscales (cantidad)',
    'Ventas no fiscales (cantidad)',
    'Pagos pendientes',
    'Margen estimado',
  ]);

  rows.forEach((r, i) => {
    csv += csvRow([
      i + 1,
      r.displayName,
      formatMoney0(r.salesTotal),
      r.salesCount,
      formatMoney0(r.ticketAverage),
      formatPct1(r.sharePct * 100),
      r.fiscalSalesCount,
      r.nonFiscalSalesCount,
      r.pendingPayments,
      formatMoney0(r.margenReposicionTotal),
    ]);
  });

  const filename = `vuelto-ranking-personal-${periodLabel.replace(/\s+/g, '_')}.csv`;
  await exportCsv(filename, csv, 'Exportar ranking de personal');
}

export async function exportResumenCsv(params: {
  periodLabel: string;
  facturacion: number;
  ventasCount: number;
  ticketPromedio: number;
  /** `null` = KPIs de reposición no cargados (error/offline): exporta 'n/d', nunca un margen ficticio. */
  margen: number | null;
  margenPct: number | null;
  gastos: number;
  gastosPct: number;
  resultadoNeto: number | null;
  resultadoNetoPct: number | null;
  ventasHoy: number;
  ventasHoyCount: number;
  syncPendientes: number;
  syncFallidos: number;
  treasuryCobraste?: number | null;
  treasuryPagasteRetiraste?: number | null;
  treasurySaldo?: number | null;
}): Promise<void> {
  const { periodLabel, ...kpi } = params;
  let csv = '';
  csv += csvRow(['Resumen — Vuelto']);
  csv += csvRow(['Período', periodLabel]);
  csv += csvRow([]);
  if (
    kpi.treasuryCobraste != null ||
    kpi.treasuryPagasteRetiraste != null ||
    kpi.treasurySaldo != null
  ) {
    csv += csvRow(['Plata del período', '', '']);
    csv += csvRow([
      'Cobraste',
      kpi.treasuryCobraste != null ? formatMoney(kpi.treasuryCobraste) : 'n/d',
      '',
    ]);
    csv += csvRow([
      'Pagaste y retiraste',
      kpi.treasuryPagasteRetiraste != null ? formatMoney(kpi.treasuryPagasteRetiraste) : 'n/d',
      '',
    ]);
    csv += csvRow([
      'Te debería quedar',
      kpi.treasurySaldo != null ? formatMoney(kpi.treasurySaldo) : 'n/d',
      '',
    ]);
    csv += csvRow([]);
  }
  csv += csvRow(['Rentabilidad del período', '', '']);
  csv += csvRow(['Indicador', 'Valor', 'Nota']);
  csv += csvRow(['Facturación', formatMoney(kpi.facturacion), `${kpi.ventasCount} ventas`]);
  csv += csvRow(['Ticket promedio', formatMoney(kpi.ticketPromedio), '']);
  csv += csvRow([
    'Margen estimado',
    kpi.margen != null ? formatMoney(kpi.margen) : 'n/d',
    kpi.margenPct != null ? `${formatPct1(kpi.margenPct * 100)} sobre ventas` : 'Sin datos de reposición',
  ]);
  csv += csvRow(['Gastos del período', formatMoney(kpi.gastos), `${formatPct1(kpi.gastosPct * 100)} sobre ventas`]);
  csv += csvRow([
    'Resultado neto',
    kpi.resultadoNeto != null ? formatMoney(kpi.resultadoNeto) : 'n/d',
    kpi.resultadoNetoPct != null ? `${formatPct1(kpi.resultadoNetoPct * 100)} sobre ventas` : 'Sin datos de reposición',
  ]);
  csv += csvRow(['Ventas hoy', formatMoney(kpi.ventasHoy), `${kpi.ventasHoyCount} ventas`]);
  csv += csvRow(['Sincronización pendiente', kpi.syncPendientes + kpi.syncFallidos, `${kpi.syncPendientes} pendientes · ${kpi.syncFallidos} fallidos`]);

  const filename = `vuelto-resumen-${periodLabel.replace(/\s+/g, '_')}.csv`;
  await exportCsv(filename, csv, 'Exportar resumen');
}

export async function exportComprasCsv(params: {
  periodLabel: string;
  summary: import('../domain/reportsPurchasesTypes').PurchasesSummaryReport;
  supplierPerformance: import('../domain/reportsSupplierPerformanceTypes').SupplierPerformanceRow[];
}): Promise<void> {
  const { periodLabel, summary, supplierPerformance } = params;
  let csv = '';
  csv += csvRow(['Compras — Vuelto']);
  csv += csvRow(['Período', periodLabel]);
  csv += csvRow([]);
  csv += csvRow(['Indicador', 'Valor']);
  csv += csvRow(['Total comprado', formatMoney0(summary.totalPurchased)]);
  csv += csvRow(['Devoluciones', formatMoney0(summary.totalReturns)]);
  csv += csvRow(['Neto comprado', formatMoney0(summary.netPurchased)]);
  csv += csvRow(['Órdenes creadas', summary.totalOrders]);
  csv += csvRow(['Recepciones', summary.totalReceptions]);
  csv += csvRow(['Gastos de proveedor', formatMoney0(summary.totalExpenses)]);

  csv += csvRow([]);
  csv += csvRow(['Proveedores']);
  csv += csvRow([
    'Proveedor',
    'Comprado',
    'Recepciones',
    'OCs',
    'OCs recibidas',
    'OCs parciales',
    'OCs canceladas',
    'Fulfillment %',
    'Entrega promedio (días)',
  ]);
  const perfBySupplier = new Map(supplierPerformance.map((p) => [p.supplierId ?? p.supplierName, p]));
  const seen = new Set<string>();
  for (const s of summary.bySupplier) {
    const key = s.supplierId ?? s.supplierName;
    seen.add(key);
    const p = perfBySupplier.get(key);
    csv += csvRow([
      s.supplierName,
      formatMoney0(s.total),
      s.receptionCount,
      p?.totalOrders ?? 0,
      p?.receivedOrders ?? 0,
      p?.partialOrders ?? 0,
      p?.cancelledOrders ?? 0,
      p ? formatPct1(p.fulfillmentRate) : '',
      p?.avgDeliveryDays != null ? p.avgDeliveryDays : '',
    ]);
  }
  for (const p of supplierPerformance) {
    const key = p.supplierId ?? p.supplierName;
    if (seen.has(key)) continue;
    csv += csvRow([
      p.supplierName,
      formatMoney0(p.totalPurchased),
      0,
      p.totalOrders,
      p.receivedOrders,
      p.partialOrders,
      p.cancelledOrders,
      formatPct1(p.fulfillmentRate),
      p.avgDeliveryDays != null ? p.avgDeliveryDays : '',
    ]);
  }

  csv += csvRow([]);
  csv += csvRow(['Compras por producto']);
  csv += csvRow(['Producto', 'Cantidad', 'Costo promedio', 'Total']);
  for (const r of summary.byProduct) {
    csv += csvRow([
      r.productName,
      formatMoney2(r.quantity),
      r.avgUnitCost != null ? formatMoney0(r.avgUnitCost) : '',
      formatMoney0(r.totalCost),
    ]);
  }

  const filename = `vuelto-compras-${periodLabel.replace(/\s+/g, '_')}.csv`;
  await exportCsv(filename, csv, 'Exportar compras');
}

export async function exportStockMermaCsv(params: {
  periodLabel: string;
  rows: import('../domain/reportsTypes').BranchStockMermaRow[];
  sumPurchased: number;
  sumSold: number;
  sumShrinkage: number;
  sumBreakage: number;
  sumExpiry: number;
  sumExpiryCost: number;
  sumShrinkageCost: number;
  page: number;
  pageSize: number;
  totalCount: number;
}): Promise<void> {
  const {
    periodLabel,
    rows,
    sumPurchased,
    sumSold,
    sumShrinkage,
    sumBreakage,
    sumExpiry,
    sumExpiryCost,
    sumShrinkageCost,
    page,
    pageSize,
    totalCount,
  } = params;
  let csv = '';
  csv += csvRow(['Merma por producto — Vuelto']);
  csv += csvRow(['Período', periodLabel]);
  csv += csvRow([]);
  csv += csvRow([
    'Producto',
    'Unidad',
    'Comprado',
    'Vendido',
    'Merma',
    'Rotura',
    'Vencidas',
    'Costo vencidas',
    'Costo merma',
    'Brecha neta',
  ]);
  for (const r of rows) {
    csv += csvRow([
      r.label,
      r.unitOfMeasure ?? '',
      formatMoney2(r.purchased),
      formatMoney2(r.sold),
      formatMoney2(r.shrinkage),
      formatMoney2(r.breakage),
      formatMoney2(r.expiry),
      formatMoney0(r.expiryCost),
      formatMoney0(r.shrinkageCost),
      formatMoney2(r.netGap),
    ]);
  }
  csv += csvRow([]);
  csv += csvRow([
    'Totales',
    '',
    formatMoney2(sumPurchased),
    formatMoney2(sumSold),
    formatMoney2(sumShrinkage),
    formatMoney2(sumBreakage),
    formatMoney2(sumExpiry),
    formatMoney0(sumExpiryCost),
    formatMoney0(sumShrinkageCost),
    '',
  ]);
  csv += csvRow(['Total productos', totalCount]);
  csv += csvRow(['Página', `${page + 1} (${pageSize} por página)`]);

  const filename = `vuelto-merma-${periodLabel.replace(/\s+/g, '_')}.csv`;
  await exportCsv(filename, csv, 'Exportar merma por producto');
}

type ModifierConsumptionCsvRow = {
  item_product_name: string | null;
  choice_name: string | null;
  group_name: string | null;
  linked_product_name: string | null;
  station_name: string | null;
  consumed_qty: number | null;
  unlinked_qty: number | null;
  reverted_qty: number | null;
  sales_count: number | null;
};

type ModifierReconciliationCsvRow = {
  linked_product_name: string | null;
  ledger_qty: number | null;
  moved_qty: number | null;
  diff: number | null;
};

function csvQty(n: number | null | undefined): string {
  const v = n ?? 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
}

export function buildModifierConsumptionCsv(params: {
  periodLabel: string;
  rows: ModifierConsumptionCsvRow[];
  reconciliation: ModifierReconciliationCsvRow[];
}): string {
  const { periodLabel, rows, reconciliation } = params;
  let csv = '';
  csv += csvRow(['Consumo de adicionales — Vuelto']);
  csv += csvRow(['Período', periodLabel]);
  csv += csvRow([]);
  csv += csvRow([
    'Opción',
    'Grupo',
    'Ítem',
    'Insumo vinculado',
    'Estación',
    'Consumido',
    'Sin stock',
    'Revertido',
    'Ventas',
  ]);
  for (const r of rows) {
    csv += csvRow([
      r.choice_name ?? '',
      r.group_name ?? '',
      r.item_product_name ?? '',
      r.linked_product_name ?? 'sin insumo vinculado',
      r.station_name ?? '',
      csvQty(r.consumed_qty),
      csvQty(r.unlinked_qty),
      csvQty(r.reverted_qty),
      r.sales_count ?? 0,
    ]);
  }

  if (reconciliation.length) {
    csv += csvRow([]);
    csv += csvRow(['Reconciliación con movimientos de stock']);
    csv += csvRow(['Insumo', 'Movido (stock)', 'Ledger', 'Diferencia']);
    for (const r of reconciliation) {
      csv += csvRow([
        r.linked_product_name ?? '',
        csvQty(r.moved_qty),
        csvQty(r.ledger_qty),
        csvQty(r.diff),
      ]);
    }
  }

  return csv;
}

export async function exportModifierConsumptionCsv(params: {
  periodLabel: string;
  rows: ModifierConsumptionCsvRow[];
  reconciliation: ModifierReconciliationCsvRow[];
}): Promise<void> {
  const csv = buildModifierConsumptionCsv(params);
  const filename = `vuelto-consumo-adicionales-${params.periodLabel.replace(/\s+/g, '_')}.csv`;
  await exportCsv(filename, csv, 'Exportar consumo de adicionales');
}
